/** Overview report tab showing report metadata */
import { IonCardContent, IonCol, IonGrid, IonList, IonRow, IonSpinner, IonText } from '@ionic/react';
import { informationCircleOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { CardHeader } from '@/components/CardHeader';
import { Card } from '@/components/Cards';
import { ClientSelectionCard } from '@/components/Cards/ClientSelectionCard';
import { SessionSelectionCard } from '@/components/Cards/SessionSelectionCard';
import { ClientModalContent } from '@/components/Clients/ClientModalContent';
import { CreateSessionModalContent } from '@/components/CreateSessionModalContent/CreateSessionModalContent';
import { HighlightAlinea } from '@/components/HighlightAlinea/HighlightAlinea';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal/Modal';
import { toast } from '@/components/Toaster/Toast';

import { convertDate, mergeClassNames } from '@/utils';
import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';
import { getFirebaseSessionTypesByOrganizationId } from '@/utils/getFirebaseSessionTypesByOrganizationId';

import ClientSessionManager from '@/hooks/useClients/sessionManager';
import UserService from '@/hooks/useClients/userService';
import { useClientSearch } from '@/hooks/useClientSearch';

import { useSessionContext } from '@/contexts';
import { useAuthContext } from '@/contexts/authContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { ExportedClient, FirebaseTimestamp, ReportType, Session } from '@/models';
import { FileInfo } from '@/models/reportV2.model';

import styles from './OverviewReportTab.module.css';

type OverviewReportProps = {
  id: string;
  fileInfo: FileInfo;
  reportType: ReportType;
};

/** Overview report tab showing report metadata
 * @param report - The full report data, including reportInfo data
 */
export const OverviewReportTab = ({ id, fileInfo, reportType }: OverviewReportProps) => {
  const [loading, setLoading] = useState(false);

  const uploadDate = new Date(fileInfo.uploadTimestamp * 1000);
  const uploadDateString = uploadDate?.toLocaleString();

  // Client Specific code
  const user = useAuthContext().state.userState?.user;
  const { userInfo } = useUserInfoContext();
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientsList, setClientsList] = useState<ExportedClient[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedSession, setSelectedSession] = useState<string>('');
  const { sessionsList, setSessionsList, isLoading, setIsLoading } = useSessionContext();
  const [sessionTypes, setSessionTypes] = useState<SessionTypeInDatabase[]>();

  const handleClientSelection = (clientId: string) => {
    setSelectedClient(clientId === selectedClient ? selectedClient : clientId);
  };

  const handleSessionSelection = (sessionId: string) => {
    setSelectedSession(sessionId === selectedSession ? selectedSession : sessionId);
  };

  const searchOptions = {
    includeScore: true,
    useExtendedSearch: true,
    threshold: 0.2,
    keys: ['firstName', 'lastName', 'email'],
  };

  const { onSearch, query, searchResult } = useClientSearch<ExportedClient>(searchOptions, clientsList);

  const [alreadyAssigned, setAlreadyAssigned] = useState<{ clientId: string; sessionId: string } | null>(null);

  useEffect(() => {
    let isMounted = true;
    ClientSessionManager.checkAlreadyAssigned(userInfo.id, id).then((result) => {
      if (isMounted) {
        setAlreadyAssigned(result);
        setSelectedClient(result?.clientId || '');
        setSelectedSession(result?.sessionId || '');
      }
    });
    return () => {
      isMounted = false;
    };
  }, [userInfo.id, id]);

  console.log('Already assigned to clientId, sessionId:', alreadyAssigned?.clientId, alreadyAssigned?.sessionId);

  useEffect(() => {
    if (!user?.uid) {
      toast({
        variant: 'error',
        title: 'Could not load sessions',
        description: 'Please contact support',
      });
      setIsLoading(false);
      return;
    }

    // If sessions are already in context, don't fetch again
    if (sessionsList.length > 0 && sessionsList?.[0].clientId === selectedClient) {
      console.log('Sessions already loaded from storage, skipping fetch.');
      setIsLoading(false);
      return;
    }

    console.log('Fetching sessions from Firestore...');

    setIsLoading(true);

    const unsubscribe = ClientSessionManager.getSessions(
      user.uid,
      selectedClient,
      userInfo.organization_id,
      (sessions, error) => {
        if (error) {
          toast({
            variant: 'error',
            title: 'Could not load sessions',
            description: error.message,
          });
          setIsLoading(false);
          return;
        }

        if (sessions.length > 0) {
          setSessionsList(sessions);
        }

        setIsLoading(false);
      },
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid, selectedClient, setSessionsList, setIsLoading, userInfo.organization_id]);

  useEffect(() => {
    setIsLoadingClients(true);
    const unsubscribeClients = UserService.getClients(user!.uid, (clients) => {
      setClientsList(clients);
      setIsLoadingClients(false);
    });

    return () => unsubscribeClients();
  }, [user]);

  useEffect(() => {
    async function getSessionTypes() {
      const sessionTypes = await getFirebaseSessionTypesByOrganizationId(userInfo.organization_id);

      setSessionTypes(sessionTypes);
    }

    getSessionTypes();
  }, [userInfo.organization_id]);

  useEffect(() => {
    console.log('Selected Session:', selectedSession);
  }, [selectedSession]);

  // #region Client Modal
  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: new Date().toISOString(),
    email: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Handle input change and reset errors when the user types
  const handleInputChange = (field: string, value: string) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = { firstName: '', lastName: '', email: '' };
    let isValid = true;

    if (!clientData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }

    if (!clientData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!clientData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(clientData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setClientData({ firstName: '', lastName: '', dateOfBirth: new Date().toISOString(), email: '' });
    setErrors({ firstName: '', lastName: '', email: '' });
  };

  // Handle Confirm button
  const handleConfirm = async () => {
    if (validateForm()) {
      setCreatingClient(true);

      try {
        await UserService.addClient(clientData);
        setIsAddClientModalOpen(false);

        toast({
          variant: 'success',
          title: 'Client added successfully',
        });
      } catch (error: any) {
        if (error.message === 'A user with this email already exists.') {
          setErrors((prev) => ({ ...prev, email: error.message }));
        } else {
          toast({
            variant: 'error',
            title: 'Error adding client',
            description: error.message,
          });
        }
      } finally {
        setCreatingClient(false);
        resetForm();
      }
    }
  };

  const handleCloseModal = () => {
    setIsAddClientModalOpen(false);
    resetForm();
  };

  // #endregion Client Modal

  // #region Session Modal
  const [isAddSessionModalOpen, setIsAddSessionModalOpen] = useState(false);
  const isDisabled = !selectedClient;
  const [creatingSession, setCreatingSession] = useState(false);
  const [sessionData, setSessionData] = useState({
    date: new Date().toISOString(),
  });

  const handleSessionInputChange = (field: string, value: string) => {
    setSessionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSessionConfirm = async () => {
    setCreatingSession(true);
    const payload = {
      date: sessionData.date,
      clientId: selectedClient,
      therapistId: user!.uid,
      sessionTypeId: 'PIPlNHQsbMDtWYJhJWAP', // Gait Observations
    };
    try {
      setSelectedSession(await UserService.createSession(payload));
      setIsAddSessionModalOpen(false);
      setSessionData({ date: new Date().toISOString() });
    } catch (error: any) {
      console.log('Error creating session', error);
    } finally {
      setCreatingSession(false);
    }
  };

  const handleAssignReportToSession = async () => {
    if (!selectedSession || !selectedClient) {
      toast({
        variant: 'error',
        title: 'Please select a client and session',
      });
      return;
    }

    setLoading(true);
    try {
      await UserService.assignReportToSession(user!.uid, selectedSession, id);
    } catch (error: any) {
      console.log('Error assigning report to session:', error);
    } finally {
      setLoading(false);
    }
  };

  // #endregion Session Modal

  // TODO: If header has backTarget, don't show the overview report tab

  if (!fileInfo) {
    return <IonText>Data not found.</IonText>;
  }

  return (
    <>
      <Modal
        footer={
          <>
            <Button onClick={() => setIsAddSessionModalOpen(false)} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={() => handleSessionConfirm()} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Add new session'
        isOpen={isAddSessionModalOpen}
        onDidDismiss={() => setIsAddSessionModalOpen(false)}>
        <CreateSessionModalContent
          loading={creatingSession}
          onInputChange={handleSessionInputChange}
          sessionData={sessionData}
        />
      </Modal>
      <Modal
        className={styles.addClientModal}
        footer={
          <>
            <Button onClick={handleCloseModal} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={handleConfirm} variant='primary'>
              Add client
            </Button>
          </>
        }
        header='Add new client'
        isOpen={isAddClientModalOpen}
        onDidDismiss={handleCloseModal}>
        <ClientModalContent
          clientData={clientData}
          errors={errors}
          loading={creatingClient}
          mode='create'
          onInputChange={handleInputChange}
        />
      </Modal>

      <IonRow className='ion-no-padding'>
        <Card style={{ margin: 0, height: '100%' }}>
          <CardHeader info={false} title={`Assign ${fileInfo.filename} (${uploadDateString}) to client & session`} />
          <IonCardContent>
            <IonGrid className='ion-no-padding' fixed>
              {/* if the report is already assigned show a message */}
              {alreadyAssigned && (
                <HighlightAlinea icon='bulb' title='Report is already assigned'>
                  <>
                    This report is already assigned to{' '}
                    <strong>
                      {clientsList.find((client) => client.id === alreadyAssigned.clientId)?.firstName || 'Client'}{' '}
                      {clientsList.find((client) => client.id === alreadyAssigned.clientId)?.lastName || ''}
                    </strong>{' '}
                    for session on{' '}
                    <strong>
                      {(() => {
                        const session = sessionsList.find((session) => session.id === alreadyAssigned.sessionId);
                        if (session) {
                          const sessionDate = convertDate(session.date as FirebaseTimestamp);
                          return sessionDate;
                        }
                        return 'Session';
                      })()}
                    </strong>
                    . To assign to a different client or session, select them below.
                  </>
                </HighlightAlinea>
              )}

              <IonRow className='ion-no-padding ion-align-items-stretch ion-align-content-stretch'>
                <IonCol size='6'>
                  <Card style={{ margin: 'var(--spacing-sm)', height: '450px' }}>
                    <CardHeader info={true} title={'Select (or create new) client'}>
                      <Button leadingIcon='plus' onClick={() => setIsAddClientModalOpen(true)} variant='secondary' />
                    </CardHeader>
                    <IonCardContent className={'ion-no-padding'}>
                      <IonGrid className='ion-no-padding' fixed>
                        <div style={{ paddingLeft: 'var(--spacing-sm)', paddingRight: 'var(--spacing-sm)' }}>
                          <Input
                            className={styles.searchbar}
                            isSearch
                            leadingIcon='search'
                            onIonFocus={() => setSelectedClient('')}
                            onIonInput={onSearch}
                            placeholder='Search name, email...'
                            value={query}
                            variant='secondary'
                          />
                        </div>
                        <div
                          className='ion-no-padding'
                          style={{
                            overflowY: 'auto',
                            height: '354px',
                          }}>
                          {searchResult.map((client) => (
                            <>
                              <ClientSelectionCard
                                firstName={client.firstName}
                                handleClientSelection={handleClientSelection}
                                id={client.id}
                                lastName={client.lastName}
                                selectedClient={client.id === selectedClient}
                              />
                            </>
                          ))}
                        </div>
                      </IonGrid>
                    </IonCardContent>
                  </Card>
                </IonCol>
                <IonCol size='6'>
                  <Card style={{ margin: 'var(--spacing-sm)', height: '450px' }}>
                    <CardHeader info={true} title={'Select (or create new) Session'}>
                      <Button
                        disabled={isDisabled}
                        leadingIcon='plus'
                        onClick={() => setIsAddSessionModalOpen(true)}
                        variant='secondary'
                      />
                    </CardHeader>
                    <IonCardContent className={'ion-no-padding'} style={{ height: '354px' }}>
                      <IonGrid class='ion-no-padding'>
                        <div style={{ overflowY: 'auto' }}>
                          {selectedClient && sessionsList.length > 0 && (
                            <>
                              {sessionsList
                                .filter((session) => session.clientId === selectedClient)
                                .map((session, index, filteredList) => (
                                  <SessionSelectionCard
                                    handleSessionSelection={handleSessionSelection}
                                    key={session.id}
                                    selected={session.id === selectedSession}
                                    session={session}
                                    sessionIndex={index}
                                    sessionType={sessionTypes?.find((type) => type.id === session.sessionTypeId)}
                                    totalSessions={filteredList.length}
                                  />
                                ))}
                            </>
                          )}
                        </div>
                      </IonGrid>
                    </IonCardContent>
                  </Card>
                </IonCol>
              </IonRow>
              <IonRow className='ion-align-items-center' style={{ height: '75px' }}>
                <Card
                  style={{
                    marginLeft: 'var(--spacing-sm)',
                    marginRight: 'var(--spacing-sm)',
                    width: '100%',
                    marginTop: 0,
                    marginBottom: 0,
                  }}>
                  <IonCardContent className='ion-no-padding'>
                    <IonGrid className=''>
                      <IonRow
                        className='ion-justify-content-center ion-align-items-center'
                        style={{ paddingLeft: 'var(--spacing-sm)', paddingRight: 'var(--spacing-sm)' }}>
                        <IonCol size='8'>
                          Assign file <strong>{fileInfo.filename}</strong>({uploadDateString}) to: <br />
                          client:
                          <strong>
                            {' '}
                            {clientsList.find((client) => client.id === selectedClient)?.firstName ||
                              'No client selected'}{' '}
                            {clientsList.find((client) => client.id === selectedClient)?.lastName}{' '}
                          </strong>{' '}
                          <br />
                          session:
                          <strong>
                            {' '}
                            {(() => {
                              const session = sessionsList.find((session) => session.id === selectedSession);
                              if (session) {
                                const sessionDate = convertDate(session.date as FirebaseTimestamp);
                                return sessionDate;
                              }
                              return 'No session selected';
                            })()}
                          </strong>
                        </IonCol>
                        <IonCol className='ion-text-end' size='4'>
                          <Button
                            disabled={!selectedClient || !selectedSession}
                            leadingIcon='checkmark'
                            onClick={() => handleAssignReportToSession()}
                            size='medium'
                            variant='primary'>
                            {loading ? <IonSpinner name='crescent' /> : 'Assign'}
                          </Button>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCardContent>
                </Card>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </Card>
      </IonRow>
    </>
  );
};
